import { useEffect, useRef, useState } from "react";
import { Heart, Camera, Loader2, Trash2, Trophy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { uploadImage } from "@/lib/imageUpload";
import { useLeft } from "@/lib/quest";
import { UserLink } from "@/components/UserBits";

const MEDAL = ["🥇", "🥈", "🥉"];

function Submit({ onDone }) {
  const ref = useRef(null);
  const [img, setImg] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try { setImg((await uploadImage("/feed/upload", f)).url); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try { await api.post("/halloween/costume", { image: img, caption }); toast.success("Dein Kostüm ist im Rennen! 🎃"); onDone(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <section className="hw-card rounded-[2rem] p-5 text-white space-y-3" data-testid="hw-submit">
      <h2 className="font-display text-xl font-black">Zeig dein Kostüm 🧛‍♀️</h2>
      <p className="text-sm text-white/75">Ein Foto pro Mitglied. Die Community stimmt per Herz ab – bis 6 Uhr.</p>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="hw-file-input" />
      {img ? <img src={img} alt="Dein Kostüm" className="w-full max-h-80 object-cover rounded-2xl" data-testid="hw-preview" />
        : <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="w-full h-36 rounded-2xl border-2 border-dashed border-orange-300/60 flex flex-col items-center justify-center gap-2 text-orange-200 font-bold" data-testid="hw-pick-btn">
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-7 h-7" />} Kostüm-Foto wählen
          </button>}
      <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={140} placeholder="Wer oder was bist du? (optional)" className="w-full h-11 rounded-full bg-white/10 px-4 text-sm placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="hw-caption" />
      <button type="button" onClick={send} disabled={!img || busy} className="w-full h-12 rounded-full bg-gradient-to-r from-orange-400 to-orange-600 font-black disabled:opacity-50" data-testid="hw-submit-btn">Kostüm einreichen</button>
    </section>
  );
}

async function share(e) {
  const url = `${window.location.origin}/halloween#c-${e.id}`;
  const text = e.mine ? "Stimm für mein Halloween-Kostüm ab! 🎃👻 Nur bis morgen 6 Uhr:" : `Stimm für das Halloween-Kostüm von ${e.user?.name || "meinem Freund"} ab! 🎃👻 Nur bis morgen 6 Uhr:`;
  try {
    if (navigator.share) { await navigator.share({ title: "Halloween-Kostüm-Wettbewerb", text, url }); return; }
    await navigator.clipboard.writeText(`${text} ${url}`);
    toast.success("Link kopiert – schick ihn deinen Freunden! 🎃");
  } catch (err) { if (err?.name !== "AbortError") toast.error("Teilen hat nicht geklappt"); }
}

function Entry({ e, i, active, onHeart, onDelete }) {
  return (
    <li id={`c-${e.id}`} className="lux-card rounded-3xl overflow-hidden scroll-mt-40 target:ring-4 target:ring-orange-400" data-testid={`hw-entry-${e.id}`}>
      <div className="relative">
        <img src={e.image} alt={e.caption || "Kostüm"} className="w-full aspect-[4/5] object-cover" loading="lazy" />
        {i < 3 && e.hearts > 0 && <span className="absolute left-3 top-3 text-3xl drop-shadow" data-testid={`hw-medal-${e.id}`}>{MEDAL[i]}</span>}
      </div>
      <div className="p-3 space-y-2">
        <div className="min-w-0">
          <UserLink id={e.user_id} className="block font-bold text-sm truncate">{e.user?.name || "Mitglied"}</UserLink>
          {e.caption && <p className="text-xs text-slate-500 truncate">{e.caption}</p>}
        </div>
        <div className="flex items-center gap-2">
        {active && <button type="button" onClick={() => share(e)} className="h-9 px-3 rounded-full bg-orange-50 text-orange-600 inline-flex items-center gap-1.5 text-xs font-bold" data-testid={`hw-share-${e.id}`}><Share2 className="w-4 h-4" /> <span className={e.mine ? "hidden sm:inline" : ""}>Teilen</span></button>}
        {e.mine && active && <button type="button" onClick={onDelete} aria-label="Kostüm löschen" className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center" data-testid="hw-delete-btn"><Trash2 className="w-4 h-4" /></button>}
        <button type="button" onClick={() => onHeart(e)} disabled={e.mine || !active} className={`ml-auto h-9 px-3.5 rounded-full inline-flex items-center gap-1.5 font-black text-sm transition-transform active:scale-90 ${e.hearted ? "bg-rose-500 text-white" : "bg-rose-50 text-rose-600"} disabled:opacity-70`} data-testid={`hw-heart-${e.id}`}>
          <Heart className={`w-4 h-4 ${e.hearted ? "fill-current" : ""}`} /> {e.hearts}
        </button>
        </div>
      </div>
    </li>
  );
}

const Left = ({ end }) => <span className="tabular-nums">{useLeft(end).text}</span>;

export default function Halloween() {
  const [d, setD] = useState(null);
  const load = () => api.get("/halloween").then(({ data }) => setD(data)).catch(() => setD(false));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (d && id.startsWith("c-")) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  }, [d?.entries?.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const heart = async (e) => {
    setD((s) => ({ ...s, entries: s.entries.map((x) => (x.id === e.id ? { ...x, hearted: !x.hearted, hearts: x.hearts + (x.hearted ? -1 : 1) } : x)) }));
    try { await api.post(`/halloween/costume/${e.id}/heart`); } catch (err) { toast.error(formatErr(err)); load(); }
  };
  const del = async () => { try { await api.delete("/halloween/costume"); toast.success("Kostüm gelöscht"); load(); } catch (e) { toast.error(formatErr(e)); } };
  if (!d) return <p className="text-slate-400 text-sm" data-testid="hw-loading">Lädt …</p>;
  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-10" data-testid="halloween-page">
      <header className="hw-card rounded-[2rem] p-6 text-white relative overflow-hidden">
        <span className="absolute -right-4 -bottom-6 text-8xl hw-float" aria-hidden>🎃</span>
        <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-orange-300">Halloween-Event</p>
        <h1 className="mt-1 text-4xl sm:text-5xl font-display font-black">Grusel-Nacht 👻</h1>
        <p className="mt-2 text-white/80 text-sm max-w-md">{d.active ? <>Noch <Left end={d.end} /> – bis morgen 6 Uhr. Poste Beiträge mit dem Wort „Halloween“ für doppelte Punkte und zeig dein Kostüm!</> : "Das Halloween-Event ist vorbei – danke fürs Mitgruseln! 🖤"}</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold">
          {d.prizes.map((p, i) => <span key={i} className="px-3 h-8 inline-flex items-center rounded-full bg-white/10 ring-1 ring-white/20" data-testid={`hw-prize-${i}`}>{MEDAL[i]} {p} Punkte</span>)}
        </div>
      </header>
      {d.active && !d.has_entry && <Submit onDone={load} />}
      <section className="space-y-3" data-testid="hw-entries">
        <h2 className="flex items-center gap-2 font-display text-lg font-extrabold text-slate-800"><Trophy className="w-5 h-5 text-orange-500" /> Kostüme ({d.entries.length})</h2>
        {d.entries.length ? (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">{d.entries.map((e, i) => <Entry key={e.id} e={e} i={i} active={d.active} onHeart={heart} onDelete={del} />)}</ul>
        ) : <p className="text-sm text-slate-500 lux-card rounded-3xl p-4" data-testid="hw-empty">Noch keine Kostüme – sei die erste Person! 🦇</p>}
      </section>
    </div>
  );
}
